import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import NotificationService from "../../services/notificationService";

const handleApiError = (error, thunkAPI, defaultMsg, key = null) => {
  const apiError = error?.response?.data;
  let message = defaultMsg;

  if (apiError?.errors) {
    message = Object.values(apiError.errors).flat();
  } else if (apiError?.message) {
    message = apiError.message;
  }

  if (key) {
    return thunkAPI.rejectWithValue({ key, error: message });
  }

  return thunkAPI.rejectWithValue(Array.isArray(message) ? message : [message]);
};

export const fetchAllNotifications = createAsyncThunk("notification/fetchAllNotifications", async ({ params, key }, thunkAPI) => {
  try {
    const res = await NotificationService.getAllNotifications(params);
    return {
      key,
      data: res.data?.data?.notificationList || [],
      totalRecords: res.data?.data?.totalUnread || 0
    };
  } catch (e) {
    return handleApiError(e, thunkAPI, "Failed to fetch notifications", key);
  }
});

export const createNotification = createAsyncThunk("notification/createNotification", async (data, thunkAPI) => {
  try {
    return (await NotificationService.createNotification(data)).data.data;
  } catch (e) {
    return handleApiError(e, thunkAPI, "Failed to create notification");
  }
});

export const markNotificationAsRead = createAsyncThunk("notification/markNotificationAsRead", async ({ notificationId, userId }, thunkAPI) => {
  try {
    await NotificationService.markNotificationAsRead(notificationId, userId);
    return { notificationId, userId };
  } catch (e) {
    return handleApiError(e, thunkAPI, "Failed to mark notification as read");
  }
});

export const markAllNotificationsAsRead = createAsyncThunk("notification/markAllNotificationsAsRead", async ({ userId, roleName }, thunkAPI) => {
  try {
    await NotificationService.markAllNotificationsAsRead(userId, roleName);
    return { userId, roleName };
  } catch (e) {
    return handleApiError(e, thunkAPI, "Failed to mark all notifications as read");
  }
});

const notificationSlice = createSlice({
  name: "notification",
  initialState: {
    listDataByKey: {},
    totalByKey: {},
    listLoadingByKey: {},
    entityByKey: {},
    entityLoadingByKey: {},
    entityErrorByKey: {},
    loading: false,
    isSuccess: false,
    errorMessage: null,
  },
  reducers: {
    resetStatus: (state) => {
      state.isSuccess = false;
      state.errorMessage = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const { notificationId } = action.payload;
        // Update notification status in state if needed
        Object.keys(state.listDataByKey).forEach((key) => {
          if (state.listDataByKey[key]) {
            state.listDataByKey[key] = state.listDataByKey[key].map((notification) =>
              notification.id === notificationId ? { ...notification, isRead: true } : notification
            );
          }
        });
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state, action) => {
        // Mark all notifications as read in state
        Object.keys(state.listDataByKey).forEach((key) => {
          if (state.listDataByKey[key]) {
            state.listDataByKey[key] = state.listDataByKey[key].map((notification) => ({
              ...notification,
              isRead: true,
            }));
          }
        });
      })
      .addMatcher(
        (action) => action.type.endsWith("/pending") && action.meta.arg?.key,
        (state, action) => {
          const key = action.meta.arg.key;
          state.listLoadingByKey[key] = true;
          state.entityLoadingByKey[key] = true;
          state.entityErrorByKey[key] = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith("/fulfilled") && action.payload?.key,
        (state, action) => {
          const { key, data, totalRecords } = action.payload;
          if (Array.isArray(data)) {
            state.listDataByKey[key] = data;
            state.totalByKey[key] = totalRecords || 0;
            state.listLoadingByKey[key] = false;
          } else {
            state.entityByKey[key] = data;
            state.entityLoadingByKey[key] = false;
          }
        }
      )
      .addMatcher(
        (action) => action.type.endsWith("/rejected") && action.payload?.key,
        (state, action) => {
          const key = action.payload.key;
          state.listLoadingByKey[key] = false;
          state.entityLoadingByKey[key] = false;
          state.entityErrorByKey[key] = action.payload.error;
        }
      )
      .addMatcher(
        (action) => (action.type.includes("create") || action.type.includes("mark")) && action.type.endsWith("/pending"),
        (state, action) => {
          state.loading = true;
          state.isSuccess = false;
        }
      )
      .addMatcher(
        (action) => (action.type.includes("create") || action.type.includes("mark")) && action.type.endsWith("/fulfilled"),
        (state, action) => {
          state.loading = false;
          state.isSuccess = true;
          state.errorMessage = null;
        }
      )
      .addMatcher(
        (action) => (action.type.includes("create") || action.type.includes("mark")) && action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.isSuccess = false;
          state.errorMessage = action.payload;
        }
      );
  },
});

export const { resetStatus } = notificationSlice.actions;

// Selectors
const EMPTY_ARRAY = [];

export const selectNotificationList = (state, userId) => state.notification.listDataByKey[`headerNotifications_${userId}`] || EMPTY_ARRAY;

export default notificationSlice.reducer;
